import { useCallback, useEffect, useMemo, useReducer } from 'react';

import {
  Categories,
  NativeResources,
  Resources,
} from '@ariksa/inventory-core/api';
import dayjs from 'dayjs';
import { filter, find, includes, isEmpty, map } from 'lodash';
import isArray from 'lodash/isArray';
import { useDispatch, useSelector } from 'react-redux';

import { getPublishedTime } from 'components/DataEntry';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import {
  exploitableOptions,
  internetExposureOptions,
  packageNameOptions,
  packageTypeOptions,
  patchAvailableOptions,
  vulnerabilitySeverityOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';
import {
  CONTAINER_ON_VIRTUAL_MACHINE,
  VulnerabilityType,
} from 'containers/Visibility/Vulnerabilities/types';
import { useSearchParams } from 'hooks/useSearchParams';

export const useVulnerabilities = (useVulnerabilityV3 = false) => {
  const {
    vulnerabilities,
    sort,
    vulnerabilityType,
    cves,
    publishedAt,
    resourceType,
  } = useSelector(selectVulnerabilities);
  const { finalSearchTerm: searchTerm } = useSelector(selectSharedState);
  const { environmentId, accountId } = useAccessBoundary();
  const dispatch = useDispatch();
  const { updateValueInURL } = useSearchParamFunctions();

  const params = useSearchParams<{
    tab: string;
    search_term: string;
    cve_id: string;
  }>();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      isExpanded: false,
      severity: vulnerabilitySeverityOptions[0],
      severities: [],
      packageName: {},
      packageType: packageTypeOptions('')[0],
      isExploitable: exploitableOptions[0],
      isInternetExposed: internetExposureOptions[0],
      isPatchable: patchAvailableOptions[0],
      knownExploited: undefined,
    },
  );

  useEffect(() => {
    if (!!params.search_term || !!params.cve_id) {
      dispatch(
        sharedStateActions.updateFinalSearchTerm(
          params.search_term || params.cve_id,
        ),
      );
      dispatch(
        sharedStateActions.updateSearchTerm(
          params.search_term || params.cve_id,
        ),
      );
    }
  }, [params.search_term, dispatch, params.cve_id]);

  //set is patchable
  useEffect(() => {
    if (!!params.isPatchable)
      updateState({
        isPatchable:
          params.isPatchable === 'yes' || params.isPatchable === 'True'
            ? patchAvailableOptions[1]
            : params.isPatchable === 'no' || params.isPatchable === 'False'
            ? patchAvailableOptions[2]
            : patchAvailableOptions[0],
      });
    else updateState({ isPatchable: patchAvailableOptions[0] });
  }, [params.isPatchable]);

  //set is exploitable
  useEffect(() => {
    if (!!params.isExploitable)
      updateState({
        isExploitable:
          params.isExploitable === 'yes' || params.isExploitable === 'True'
            ? exploitableOptions[1]
            : exploitableOptions[2],
      });
    else updateState({ isExploitable: exploitableOptions[0] });
  }, [params.isExploitable]);

  //set is internet exposed
  useEffect(() => {
    if (!!params.isInternetExposed)
      updateState({
        isInternetExposed:
          params.isInternetExposed === 'yes' ||
          params.isInternetExposed === 'True'
            ? internetExposureOptions[1]
            : internetExposureOptions[2],
      });
    // Internet exposure has its own options (placeholder label differs).
    else updateState({ isInternetExposed: internetExposureOptions[0] });
  }, [params.isInternetExposed]);

  //set known exploited
  useEffect(() => {
    updateState({
      knownExploited:
        params.known_exploited === 'true' || params.known_exploited === 'True'
          ? true
          : undefined,
    });
  }, [params.known_exploited]);

  //set severity
  useEffect(() => {
    if (!!params.severity)
      updateState({
        severity:
          find(vulnerabilitySeverityOptions, { value: params.severity }) ||
          vulnerabilitySeverityOptions[0],
      });
    else updateState({ severity: vulnerabilitySeverityOptions[0] });
  }, [params.severity]);

  //set packageType
  useEffect(() => {
    if (!!params.packageType)
      updateState({
        packageType:
          find(packageTypeOptions(''), { value: params.packageType }) ||
          packageTypeOptions('')[0],
      });
    else updateState({ packageType: packageTypeOptions('')[0] });
  }, [params.packageType]);

  //set packageName
  useEffect(() => {
    if (!!params.packageName)
      updateState({
        packageName:
          find(packageNameOptions(params.packageType), {
            value: params.packageName,
          }) || packageNameOptions(params.packageType)[0],
      });
    else
      updateState({ packageName: packageNameOptions(params.packageType)[0] });
  }, [params.packageName, params.packageType]);

  //set severity
  useEffect(() => {
    if (!!params.severities) {
      // Drill-down links pass lowercase values; the options are uppercase.
      const wanted = String(params.severities)
        .toLowerCase()
        .split(',')
        .map(value => value.trim());
      updateState({
        severities: filter(
          vulnerabilitySeverityOptions,
          o => !!o.value && includes(wanted, o.value.toLowerCase()),
        ),
      });
    } else updateState({ severities: [] });
  }, [params.severities]);

  const updateValue = useCallback(
    (field, value) => {
      updateValueInURL(
        field,
        isArray(value) ? map(value, o => o?.value)?.join(',') : value?.value,
      );
    },
    [updateValueInURL],
  );

  // ScanVault accepts several severities; the drill-down links from the
  // summary widgets pass them as `severities`.
  const scanVaultSeverities = useMemo(
    () =>
      !isEmpty(state.severities)
        ? map(state.severities, o => o.value)
        : state.severity?.value
        ? [state.severity.value]
        : undefined,
    [state.severities, state.severity],
  );

  const getCVEVulnerabilities = useCallback(() => {
    const cvePublished = !!publishedAt?.value
      ? {
          publishedAt: dayjs()
            .subtract(publishedAt?.value, 'days')
            .utc()
            .format('YYYY-MM-DD'),
        }
      : {};
    const exploitable =
      state.isExploitable?.value === 'yes'
        ? true
        : state.isExploitable?.value === 'no'
        ? false
        : undefined;
    dispatch(
      actions.getCveVulnerabilities({
        q: {
          // environmentId,
          // accountId: accountId ? [accountId] : [],
          page: cves.page.info.page_number,
          pageSize: cves.page.info.page_size,
          searchTerm: !searchTerm ? undefined : searchTerm,
          sortOrder: sort.sortOrder,
          sortBy:
            sort.sortField === 'cvss_v3_score' ? 'cvss_score' : sort.sortField,
          exploitable,
          severity: useVulnerabilityV3
            ? scanVaultSeverities
            : state.severity?.value,
          ...(useVulnerabilityV3
            ? {
                knownExploited: state.knownExploited,
                isPatchable:
                  state.isPatchable?.value === 'yes'
                    ? true
                    : state.isPatchable?.value === 'no'
                    ? false
                    : undefined,
              }
            : {}),
          globalView: false,
          useScanVaultVulnerabilityFindings: useVulnerabilityV3,
          //resolved: tablesTabIndex === 1,
          ...cvePublished,
        },
      }),
    );
  }, [
    dispatch,
    state.severity,
    cves.page.info,
    searchTerm,
    sort,
    state.isExploitable,
    state.isPatchable,
    state.knownExploited,
    scanVaultSeverities,
    publishedAt,
    useVulnerabilityV3,
  ]);

  const getResourceVulnerabilities = useCallback(() => {
    const published =
      vulnerabilityType === VulnerabilityType.Resolved
        ? { resolved_at: getPublishedTime(publishedAt?.value) }
        : { effective_start: getPublishedTime(publishedAt?.value) };
    const patchable =
      state.isPatchable?.value === 'yes'
        ? true
        : state.isPatchable?.value === 'no'
        ? false
        : undefined;
    const exploitable =
      state.isExploitable?.value === 'yes'
        ? true
        : state.isExploitable?.value === 'no'
        ? false
        : undefined;
    const severityFilter = { severity: state.severity?.value };

    switch (resourceType) {
      case Resources.VirtualMachine:
      case Resources.ServerlessCompute:
      case Resources.Image:
      case NativeResources.ElasticContainerService:
      case CONTAINER_ON_VIRTUAL_MACHINE:
        let deployed = {};
        let resource = resourceType as Resources;
        if (
          resourceType === CONTAINER_ON_VIRTUAL_MACHINE ||
          resourceType === NativeResources.ElasticContainerService
        ) {
          deployed = {
            deployed_in:
              resourceType === CONTAINER_ON_VIRTUAL_MACHINE
                ? Resources.VirtualMachine
                : 'ContainerService',
          };
          resource = Resources.Containers;
        }
        dispatch(
          actions.getVulnerabilities({
            q: {
              vulnerabilityInsightRequest: {
                environment_id: environmentId,
                account_id: accountId ? [accountId] : [],
                agnostic_resource: resource,
                page: vulnerabilities.page.info.page_number,
                size: vulnerabilities.page.info.page_size,
                expanded: true,
                search_term: searchTerm,
                category:
                  resourceType === NativeResources.ElasticContainerRegistry
                    ? Categories.ContainerService
                    : Categories.Compute,
                ...deployed,
                resolved: vulnerabilityType === VulnerabilityType.Resolved,
                is_patchable: patchable,
                exploitable: exploitable,
                internet_exposed_vm: useVulnerabilityV3
                  ? undefined
                  : state.isInternetExposed?.value === 'yes'
                  ? true
                  : state.isInternetExposed?.value === 'no'
                  ? false
                  : undefined,
                ...severityFilter,
                ...(useVulnerabilityV3
                  ? {
                      severities: scanVaultSeverities,
                      known_exploited: state.knownExploited,
                    }
                  : {}),
                //...type,
                //...name,
                ...published,
              },
              useScanVaultVulnerabilityFindings: useVulnerabilityV3,
            } as any,
            onSuccess: res => {
              if (!isEmpty(res?.items)) {
                if (
                  resourceType === Resources.VirtualMachine ||
                  resource === Resources.ServerlessCompute
                ) {
                  dispatch(
                    actions.getRiskContext({
                      q: {
                        riskContextRequest: {
                          uuids: map(res?.items, o => o?.uuid),
                        },
                      },
                    }),
                  );
                }
              }
            },
          }),
        );
        break;
      case NativeResources.ElasticContainerRegistry:
        const date =
          vulnerabilityType === VulnerabilityType.Resolved
            ? { resolvedDate: getPublishedTime(publishedAt?.value) }
            : { effectiveStart: getPublishedTime(publishedAt?.value) };
        dispatch(
          actions.getEcrImages({
            q: {
              environmentId: environmentId,
              accountId: accountId ? [accountId] : [],
              ...date,
              isPatchable: patchable,
              ...severityFilter,
              searchTerm,
              resolved: vulnerabilityType === VulnerabilityType.Resolved,
              page: vulnerabilities.page.info.page_number,
              pageSize: vulnerabilities.page.info.page_size,
              useScanVaultVulnerabilityFindings: useVulnerabilityV3,
            },
          }),
        );
        break;
    }
  }, [
    dispatch,
    resourceType,
    accountId,
    environmentId,
    vulnerabilities.page.info,
    publishedAt,
    searchTerm,
    vulnerabilityType,
    state.isPatchable,
    state.severity,
    state.isExploitable,
    state.isInternetExposed,
    state.knownExploited,
    scanVaultSeverities,
    useVulnerabilityV3,
  ]);

  const getPackageVulnerabilities = useCallback(() => {
    const patchable =
      state.isPatchable?.value === 'yes'
        ? true
        : state.isPatchable?.value === 'no'
        ? false
        : undefined;
    const exploitable =
      state.isExploitable?.value === 'yes'
        ? true
        : state.isExploitable?.value === 'no'
        ? false
        : undefined;
    const internetExposedVm = useVulnerabilityV3
      ? undefined
      : state.isInternetExposed?.value === 'yes'
      ? true
      : state.isInternetExposed?.value === 'no'
      ? false
      : undefined;
    const severityFilter = { severity: map(state.severities, o => o.value) };

    dispatch(
      actions.getPackageVulnerabilities({
        q: {
          ...severityFilter,
          ...(vulnerabilityType === VulnerabilityType.Resolved
            ? { resolvedAt: getPublishedTime(publishedAt?.value) }
            : { effectiveStart: getPublishedTime(publishedAt?.value) }),
          environmentId,
          accountId: accountId ? [accountId] : [],
          page: vulnerabilities.page.info.page_number,
          size: vulnerabilities.page.info.page_size,
          searchTerm: !searchTerm ? undefined : searchTerm,
          packageCategory: state.packageType?.value,
          packageCategoryName: state.packageName?.value,
          resolved: vulnerabilityType === VulnerabilityType.Resolved,
          isPatchable: patchable,
          internetExposedVm,
          exploitable,
          knownExploited: state.knownExploited,
          useScanVaultVulnerabilityFindings: useVulnerabilityV3,
        } as any,
      }),
    );
  }, [
    dispatch,
    accountId,
    environmentId,
    vulnerabilities.page.info,
    publishedAt,
    searchTerm,
    vulnerabilityType,
    state.isPatchable,
    //state.severity,
    state.severities,
    state.packageName,
    state.packageType,
    state.isExploitable,
    state.isInternetExposed,
    state.knownExploited,
    useVulnerabilityV3,
    //sort,
  ]);

  return {
    getResourceVulnerabilities,
    getPackageVulnerabilities,
    searchTerm,
    isPatchable: state.isPatchable,
    packageType: state.packageType,
    packageName: state.packageName,
    severity: state.severity,
    severities: state.severities,
    isExpanded: state.isExpanded,
    isExploitable: state.isExploitable,
    isInternetExposed: state.isInternetExposed,
    updateValue,
    getCVEVulnerabilities,
  };
};
